/**
 * World 8 — "Storm Vault" (Thunder). Narrative fragment for campaignStories.ts.
 * ACT II, chapter 3 — the saga's gut-punch world. Dialogue voiced at Pokémon
 * Red trainer complexity.
 *
 * THE TALE: Vera returns to the post she deserted nine generations ago; the
 * Vault tempts Cliff with being remembered forever, and he refuses it for one
 * look from Sentry. Erosion beat #3 lands on Timmy ("cup") then Lulu (a blank
 * heartbeat at her own reflection). The Soul-Eater eats half of Chirpy's name
 * ("Get 'em, Chir...") and Timmy quits on the Vault steps. Aggie kneels beside
 * him with the counter-spell: a name is kept, not stored. Vera holds the Last
 * Lock with her own hands — kept promises are what move Boltheart, the Vault
 * Guardian (Toro). The player answers the finale alone. Second First-Soul shard.
 * Count: 15. Hooks into W9 with Timmy, packed, taking Aggie's hand.
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD8_TAGLINE =
  "Vera has returned to the door she once abandoned. Beyond it, storms rattle their locks. Then Timmy holds up a cup and can't say what it is.";

export const WORLD8_ZONE_STORIES: Record<string, string> = {
  z21: "Vera knows these stairs. She takes the first step, stops, then takes another. Gray's footprints are already above her.",
  z22: "Gray dust drifts under every locked door. Check on your friends as you pass. It is settling on them too.",
  z23: "Timmy won't leave the steps. Aggie stays with him while you follow Vera to the final door.",
};

export const WORLD8_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 21: Rising Stairs (1v1) ---
  "z21-n1": {
    story: "You hear the Vault whisper to Cliff: his name in stone, if he stays inside forever.",
    dialogue: {
      intro: "The Vault offered to carve my name on its door. Sentry pecked the door. I trust her. Battle?",
      playerWin: "I'll take a grumpy bird who knows me over a door that says it does.",
      playerLose: "Another round when you're ready. We're staying on this side of the door.",
      introRematchAfterLoss: "The offer still stands, and I still say no.",
      introRematchAfterWin: "I told the door no. One look from Sentry beats forever.",
      playerWinRepeat: "You win. Sentry's already looking up the stairs. We'd better follow.",
      playerLoseRepeat: "We win. Sentry still prefers her perch to whatever that door is offering.",
    },
  },
  "z21-n2": {
    story: "Vera climbs beside you, toward the door she once ran from.",
    dialogue: {
      intro: "I know these stairs. I know this battle stance. I'm rather out of practice at both. Ready?",
      playerWin: "Right. Up we go. You can remind me to breathe if I forget.",
      playerLose: "The spirits on this stair remember me. Not yet.",
      introRematchAfterLoss: "You came back. I am a little higher now.",
      introRematchAfterWin: "My legs remember this stair. I wish they did not.",
      playerWinRepeat: "Beaten again, and still climbing. That is the win.",
      playerLoseRepeat: "A win. Now to manage the rest of these stairs.",
    },
  },
  "z21-n3": {
    story: "Zeck eyes the Vault, then you. Storm waits on the stair behind him.",
    dialogue: {
      intro: "A vault full of trapped storms, and no owner! Storm brushes both cliff walls. He waits. Cage, you go first!",
      playerIntro: "Not this one.",
      playerWin: "Even Storm couldn't hold the stair? Back, all of you. We'll find another way in.",
      playerLose: "Ha! The Vault is mine now. Every storm in it, mine!",
      introRematchAfterLoss: "Back again? Storm paced the stair all night. Cage goes first.",
      introRematchAfterWin: "You beat Cage and Storm once. Storm hasn't stopped pacing since. Cage, go!",
      playerWinRepeat: "Cage, fall back. Storm, leave the door. We'll try another way.",
      playerLoseRepeat: "The Vault and its storms belong to Clan Z!",
    },
  },
  "z21-nw1": {
    story: "On the wet stair, something spits sparks at you.",
    dialogue: {
      intro: "A wild Zaricat springs from the stair! Its fur spits sparks!",
      playerWin: "The wild Zaricat bolts up the dark stair.",
      playerLose: "The wild Zaricat drives you back down.",
      introRematchAfterLoss: "The stair crackles. The wild Zaricat returns.",
      introRematchAfterWin: "The wild Zaricat knows your scent now. It hisses.",
      playerWinRepeat: "The wild Zaricat vanishes into the storm.",
      playerLoseRepeat: "The stair stays wild. Sparks rain down.",
    },
  },
  "z21-n4": {
    story: "A hundred stuck old locks bar your way up the next flight of stairs.",
    dialogue: {
      intro: "These doors were shut for a reason. You'd better be ready for what's inside.",
      playerWin: "The locks turn, one after another. The last one sticks, then clicks open.",
      playerLose: "Locked! As all things should stay!",
    },
  },

  // --- Zone 22: Sealed Halls (2v2) ---
  "z22-n1": {
    story: "Timmy turns to ask you for something. He reaches for the word. Nothing comes.",
    dialogue: {
      intro: "Ninety locks so far! Or nine hundred. Pass my... water-thing? Never mind. Let's battle.",
      playerWin: "I lost! Cup! The water-thing is a cup! Ha! ...Why is nobody laughing?",
      playerLose: "I win! Way to go, Chirpy! See? I still know all the big words.",
      introRematchAfterLoss: "Battle me again! Like before, when things were normal!",
      introRematchAfterWin: "Battle again? I'll get every word right. Watch. Cup. Cup. Cup!",
      playerWinRepeat: "You win again! Fine. I still remember cup.",
      playerLoseRepeat: "A win. She's still looking at me. She knows something's wrong.",
    },
  },
  "z22-n2": {
    story: "In the empty hall, you hear Mimi rehearsing a joke.",
    dialogue: {
      intro: "I've got a joke ready for Lulu. Let's battle while I remember the ending.",
      playerWin: "No fair! I forgot the joke. It was mostly about you losing, so that's convenient.",
      playerLose: "I win! Lulu missed it, though. That never happens.",
      introRematchAfterLoss: "Back? This time I bet two snacks!",
      introRematchAfterWin: "You again! I did Lulu's lines myself this time.",
      playerWinRepeat: "You win. I'd better find Lulu before I forget the whole joke.",
      playerLoseRepeat: "Another win. I miss having someone to argue about the score with.",
    },
  },
  "z22-n3": {
    story: "Lulu stares at the polished door beside you. For a second, she does not seem to know her own face.",
    dialogue: {
      intro: "I saw my face in that door. It's Mimi's face too. For a second, I didn't know it. Let's battle.",
      playerWin: "There. I remember. Me. Mimi. This team. Say something ordinary, please.",
      playerLose: "I win. Don't ask about the door.",
      introRematchAfterLoss: "It has not happened again. That counts as fine.",
      introRematchAfterWin: "Mimi's face is Mimi's face. I say it a lot now.",
      playerWinRepeat: "...You win again. I still know her face.",
      playerLoseRepeat: "I won. I remember every move. That's a relief.",
    },
  },
  "z22-n5": {
    story: "The floor shakes under your feet. Dash is that big now. He and Zip guard the sealed hall.",
    dialogue: {
      intro: "Dash can't even fit through the door now. He wants out anyway. Zeck says hold the hall. I'm backing Zeck. Probably.",
      playerIntro: "Probably? Then you can still choose, Zip.",
      playerWin: "Dash had the right idea. We're leaving!",
      playerLose: "Ha! Clan Z keeps the hall!",
      introRematchAfterLoss: "Back in my hall? Dash is stuck in here with you!",
      introRematchAfterWin: "Dash has picked a better spot this time. Less slippery. We checked.",
      playerWinRepeat: "Fine! Zeck can guard his own cage!",
      playerLoseRepeat: "Slowpoke! Clan Z rules the halls!",
    },
  },
  "z22-nw2": {
    story: "A sealed storm bursts open in front of you. Something breaks loose.",
    dialogue: {
      intro: "A wild Trixon breaks loose! Another storm answers it!",
      playerWin: "The wild Trixon swirls back into the dark like a wave.",
      playerLose: "The wild Trixon drives you from the hall.",
      introRematchAfterLoss: "The seals hum. The wild Trixon returns.",
      introRematchAfterWin: "The wild Trixon knows your squad now. It hisses steam.",
      playerWinRepeat: "The wild Trixon fades into the sealed dark.",
      playerLoseRepeat: "The hall stays wild. Storms rattle the locks.",
    },
  },
  "z22-n4": {
    story: "Far down the steps, you find Timmy alone. Aggie stops above him.",
    dialogue: {
      intro: "Timmy can't say Chirpy's name. We'll say it for him. Now, your team against mine. Something familiar.",
      playerWin: "Tell him about her. The lunch she stole. The day she caught him. Give the name something to hold on to.",
      playerLose: "Not tonight, child. Rest a while.",
      introRematchAfterLoss: "He is still sitting. I am still beside him.",
      introRematchAfterWin: "Keep saying Chirpy's name near him. That is how keeping works.",
      playerWinRepeat: "He's listening. Keep telling him the stories. I'll stay beside him.",
      playerLoseRepeat: "Rest, child. Timmy and I will be right here.",
    },
  },

  // --- Zone 23: Vault Heart (3v3) ---
  "z23-n1": {
    story: "Beside an old locked door, a sad spirit waits for you.",
    dialogue: {
      intro: "I locked this door when the island chose to forget the First Keeper. I've been sorry ever since. Can you get past me?",
      playerWin: "We were told to forget the First Keeper. We should have stayed with him.",
      playerLose: "My regret is too old for you.",
    },
  },
  "z23-n2": {
    story: "Vera plants both hands on the final seal and looks back at you.",
    dialogue: {
      intro: "I left this seal once. Not today. One battle, then help me get it open.",
      playerWin: "I've got the seal. You go on. This time, I'll be here when you come back.",
      playerLose: "I walk to that seal either way.",
      introRematchAfterLoss: "My hands are on the door. They are staying.",
      introRematchAfterWin: "The door's open. I can spare a moment for you.",
      playerWinRepeat: "Tell them I came back. Tell them I stayed.",
      playerLoseRepeat: "We'll try again. I promised to stay, remember?",
    },
  },
  "z23-n3": {
    story: "The Last Lock blazes beneath Vera's hands. Its light falls on you.",
    dialogue: {
      intro: "Behind me are the First Keeper's boots, his drawings, his cup. The island hid them all. Can you carry them?",
      playerWin: "The seal lifts. Vera braces the door with both hands. There's room to pass.",
      playerLose: "Then I hold, and so does she.",
    },
  },
  "z23-n5": {
    story: "Zap waits for you by Zeck's open cage. Shade guards the Heart.",
    dialogue: {
      intro: "When Shade flaps, every lock in the vault rattles. The cage is open. Zeck says fill it. Shade. Hold the way.",
      playerIntro: "Move aside, Shade. I'm coming through.",
      playerWin: "...Shade. Back to Zeck. This isn't finished.",
      playerLose: "Quiet wins. Clan Z holds the Heart.",
      introRematchAfterLoss: "Back in the vault. Shade watched you. He's ready.",
      introRematchAfterWin: "Another match. Just us and our teams this time.",
      playerWinRepeat: "...I step aside.",
      playerLoseRepeat: "The Heart stays shut. Clan Z holds it.",
    },
  },
  "z23-n4": {
    story: "You chase Zeck up the last stair! He runs for the shard. Boltheart, the great Toro, steps out of its carving on the door and blocks the top.",
    dialogue: {
      intro: "Boltheart was left alone at this door long ago. It won't move until someone stays. Get through while Vera holds the seal.",
      playerWin: "Zeck flees down the stair with his empty cage. Vera holds the door, and Boltheart stops holding the sky alone. Its shard makes fifteen, and the First Keeper's face glows inside it.",
      playerLose: "Boltheart drives you back down the stair. Not yet.",
      introRematchAfterLoss: "Boltheart is not so sure now. Vera's hands stay on the seal.",
      introRematchAfterWin: "Timmy stands packed at the gate. \"I'm getting her name BACK!\"",
      playerWinRepeat: "Boltheart rests. Its shard shows the First Keeper's face.",
      playerLoseRepeat: "Boltheart stamps a while longer. Vera keeps her hands on the door.",
    },
  },
};
